import React, { FC, ReactElement, useState } from 'react';
// mui components
import { Button, IconButton, Popover, SxProps, Theme } from '@mui/material';
// api
import {
  IPermissionsScope,
  PERMISSIONS_SUBJECT_ENUM,
  useGetCompanyRolePermissionsMeQuery,
} from 'redux-store/api/company-roles';
// types
import { PopperActionEnum } from 'pages/selections/utils/types';
import { SystemStyleObject } from '@mui/system';
// utils
import { stopPropagation } from 'utils/helpers';
// icons
import VerticalDotsIcon from 'assets/icons/buttons/table-dots.svg';
import EditIcon from 'assets/icons/buttons/edit.svg';
import DeleteIcon from 'assets/icons/buttons/delete.svg';
import CloneIcon from 'assets/icons/new/profile/clone.svg';
import PlusIcon from 'assets/icons/buttons/plus.svg';
// styles
import { popperStyles } from './styles';

interface ICorrespondingPermission {
  permissionSubject: PERMISSIONS_SUBJECT_ENUM;
  permissionScope: keyof IPermissionsScope;
}

export interface IActionsPopperProps {
  sx?: {
    iconButton?: SxProps<Theme>;
  };
  className?: string;
  isAddItem?: boolean;
  isEditItem?: boolean;
  isDeleteItem?: boolean;
  permissions?: Partial<Record<PopperActionEnum, ICorrespondingPermission>>;
  handleEdit?: () => void;
  handleDelete?: () => void;
  handleAddItem?: () => void;
  handleClone?: () => void;
}

export interface IPopperAction {
  id: number;
  label: PopperActionEnum;
  icon: ReactElement;
  isHidden?: boolean;
  hasPermission: boolean;
  handleClick?: () => void;
}

const ActionsPopper: FC<IActionsPopperProps> = ({
  sx = {},
  className = '',
  isAddItem = false,
  isEditItem = true,
  isDeleteItem = true,
  permissions,
  handleEdit,
  handleDelete,
  handleAddItem,
  handleClone,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const getPermissions = (action: PopperActionEnum) => {
    return (
      !permissions?.[action] ||
      (permissions?.[action] &&
        userPermissions?.[permissions[action].permissionSubject]?.[
          permissions[action].permissionScope
        ])
    );
  };

  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();

  const actions: IPopperAction[] = [
    {
      id: 1,
      label: PopperActionEnum.EDIT,
      icon: <EditIcon />,
      handleClick: handleEdit,
      isHidden: !isEditItem || !handleEdit,
      hasPermission: getPermissions(PopperActionEnum.EDIT),
    },
    {
      id: 4,
      label: PopperActionEnum.CLONE,
      icon: <CloneIcon />,
      handleClick: handleClone,
      isHidden: !handleClone,
      hasPermission: getPermissions(PopperActionEnum.ADD),
    },
    {
      id: 2,
      label: PopperActionEnum.ADD,
      icon: <PlusIcon />,
      handleClick: handleAddItem,
      isHidden: !isAddItem,
      hasPermission: getPermissions(PopperActionEnum.ADD),
    },
    {
      id: 3,
      label: PopperActionEnum.DELETE,
      icon: <DeleteIcon />,
      handleClick: handleDelete,
      isHidden: !isDeleteItem,
      hasPermission: getPermissions(PopperActionEnum.DELETE),
    },
  ];

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    stopPropagation(e);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleBtnClick = (handleClick: () => void) => {
    handleClick && handleClick();
    handleClose();
  };

  const open = Boolean(anchorEl);

  const { iconButton } = sx;

  return (
    <>
      <IconButton
        sx={[popperStyles.iconBtn, iconButton as SystemStyleObject<Theme>]}
        onClick={handleOpen}
        className={className}
      >
        <VerticalDotsIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={popperStyles.popover}
        onClick={stopPropagation}
      >
        {/* {isAddItem && (
          <Button
            sx={popperStyles.actionBtn}
            startIcon={<PlusIcon />}
            onClick={() => handleBtnClick(handleAddItem)}
          >
            add item
          </Button>
        )} */}
        {actions.map((action) =>
          !action.isHidden && action.hasPermission ? (
            <Button
              key={action.id}
              sx={popperStyles.actionBtn}
              startIcon={action.icon}
              onClick={() => handleBtnClick(action.handleClick)}
            >
              {action.label}
            </Button>
          ) : null
        )}
      </Popover>
    </>
  );
};

export default ActionsPopper;
